/**
 * Column mapping: match a file's headers to the fields an importer expects.
 *
 * Matching tiers, strongest first:
 *   exact              header === field key / label / alias                       confidence 1
 *   case-insensitive   same text ignoring case, or ignoring spaces/underscores/    0.95 / 0.9
 *                      punctuation/accents ("E-mail" ↔ "email", "First Name" ↔ first_name)
 *   fuzzy              edit distance, token containment, substring                 0.72 – 0.89
 *
 * Each field and each column is used at most once (strongest match wins, ties go to the leftmost column).
 * Fuzzy matches come back `confirmed: false`: they are suggestions, and the UI must make the user accept
 * them (or pick another field) before the import can proceed. Nothing fuzzy is applied silently.
 */

import type { ColumnMapping, ColumnMappingEntry, CsvTargetField, MapColumnsOptions, MatchMethod } from "./types";

const DEFAULT_FUZZY_THRESHOLD = 0.72;
const FUZZY_CAP = 0.89;

function normalise(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "");
}

function tokens(text: string): string[] {
  return text
    .replace(/([a-z\d])([A-Z])/g, "$1 $2")
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((token) => token.length > 0);
}

/** Edit distance where swapping two adjacent characters costs 1 (the most common typo). */
function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const rows: number[][] = [];
  for (let i = 0; i <= a.length; i++) {
    const row: number[] = new Array(b.length + 1).fill(0);
    row[0] = i;
    rows.push(row);
  }
  const first = rows[0] as number[];
  for (let j = 0; j <= b.length; j++) first[j] = j;

  for (let i = 1; i <= a.length; i++) {
    const row = rows[i] as number[];
    const prev = rows[i - 1] as number[];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let best = Math.min((row[j - 1] as number) + 1, (prev[j] as number) + 1, (prev[j - 1] as number) + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        best = Math.min(best, ((rows[i - 2] as number[])[j - 2] as number) + 1);
      }
      row[j] = best;
    }
  }
  return (rows[a.length] as number[])[b.length] as number;
}

function fuzzyScore(header: string, candidate: string): number {
  const nh = normalise(header);
  const nc = normalise(candidate);
  if (nh.length === 0 || nc.length === 0) return 0;

  const longest = Math.max(nh.length, nc.length);
  let score = 1 - editDistance(nh, nc) / longest;

  const th = new Set(tokens(header));
  const tc = new Set(tokens(candidate));
  if (th.size > 0 && tc.size > 0) {
    const [small, large] = th.size <= tc.size ? [th, tc] : [tc, th];
    if ([...small].every((token) => large.has(token))) {
      score = Math.max(score, 0.78 + 0.05 * (small.size / large.size));
    }
  }

  const [shorter, longer] = nh.length <= nc.length ? [nh, nc] : [nc, nh];
  if (shorter.length >= 4 && longer.includes(shorter)) score = Math.max(score, 0.74);

  return Math.min(score, FUZZY_CAP);
}

interface Candidate {
  column: number;
  field: string;
  fieldOrder: number;
  confidence: number;
  method: Exclude<MatchMethod, "none" | "manual">;
}

function bestForPair(
  header: string,
  field: CsvTargetField,
  threshold: number
): Omit<Candidate, "column" | "field" | "fieldOrder"> | null {
  const names = [field.key, field.label, ...(field.aliases ?? [])];
  const trimmed = header.trim();
  const lower = trimmed.toLowerCase();
  const squashed = normalise(trimmed);

  let best: Omit<Candidate, "column" | "field" | "fieldOrder"> | null = null;
  const consider = (confidence: number, method: Candidate["method"]) => {
    if (best === null || confidence > best.confidence) best = { confidence, method };
  };

  for (const name of names) {
    if (name === trimmed) {
      consider(1, "exact");
    } else if (name.toLowerCase() === lower) {
      consider(0.95, "case-insensitive");
    } else if (squashed.length > 0 && normalise(name) === squashed) {
      consider(0.9, "case-insensitive");
    } else {
      const score = fuzzyScore(trimmed, name);
      if (score >= threshold) consider(score, "fuzzy");
    }
  }
  return best;
}

/**
 * Suggest a field for each column. Returns one entry per header, in file order.
 *
 * @example
 * ```ts
 * mapColumns(["E-mail", "Full Nme"], [
 *   { key: "email", label: "Email" },
 *   { key: "name", label: "Full name" },
 * ]);
 * // → [{ column: 0, field: "email", method: "case-insensitive", confirmed: true, … },
 * //    { column: 1, field: "name",  method: "fuzzy",            confirmed: false, … }]
 * ```
 */
export function mapColumns(
  headers: readonly string[],
  fields: readonly CsvTargetField[],
  options: MapColumnsOptions = {}
): ColumnMapping {
  const threshold = options.fuzzyThreshold ?? DEFAULT_FUZZY_THRESHOLD;
  const candidates: Candidate[] = [];

  headers.forEach((header, column) => {
    fields.forEach((field, fieldOrder) => {
      const hit = bestForPair(header, field, threshold);
      if (hit !== null) candidates.push({ column, field: field.key, fieldOrder, ...hit });
    });
  });

  candidates.sort((a, b) => b.confidence - a.confidence || a.column - b.column || a.fieldOrder - b.fieldOrder);

  const usedColumns = new Set<number>();
  const usedFields = new Set<string>();
  const byColumn = new Map<number, Candidate>();
  for (const candidate of candidates) {
    if (usedColumns.has(candidate.column) || usedFields.has(candidate.field)) continue;
    usedColumns.add(candidate.column);
    usedFields.add(candidate.field);
    byColumn.set(candidate.column, candidate);
  }

  return headers.map((header, column): ColumnMappingEntry => {
    const match = byColumn.get(column);
    if (match === undefined) {
      return { column, header, field: null, confidence: 0, method: "none", confirmed: true };
    }
    return {
      column,
      header,
      field: match.field,
      confidence: Math.round(match.confidence * 100) / 100,
      method: match.method,
      confirmed: match.method !== "fuzzy",
    };
  });
}

/**
 * The user picked `field` (or "ignore" = `null`) for `column`. The choice is confirmed. A field can only
 * feed one column, so any other column that held it falls back to "ignore".
 */
export function setColumnField(mapping: ColumnMapping, column: number, field: string | null): ColumnMapping {
  return mapping.map((entry) => {
    if (entry.column === column) {
      return {
        ...entry,
        field,
        confidence: field === null ? 0 : 1,
        method: field === null ? "none" : "manual",
        confirmed: true,
      };
    }
    if (field !== null && entry.field === field) {
      return { ...entry, field: null, confidence: 0, method: "none", confirmed: true };
    }
    return entry;
  });
}

/** Accept a fuzzy suggestion as it stands. */
export function confirmColumn(mapping: ColumnMapping, column: number): ColumnMapping {
  return mapping.map((entry) => (entry.column === column ? { ...entry, confirmed: true } : entry));
}

/** Accept every suggestion. */
export function confirmAll(mapping: ColumnMapping): ColumnMapping {
  return mapping.map((entry) => (entry.confirmed ? entry : { ...entry, confirmed: true }));
}

/** Entries that are fuzzy suggestions the user has not accepted yet. */
export function pendingConfirmations(mapping: ColumnMapping): ColumnMappingEntry[] {
  return mapping.filter((entry) => !entry.confirmed && entry.field !== null);
}

/** Required fields no column is mapped to. */
export function missingRequired(mapping: ColumnMapping, fields: readonly CsvTargetField[]): CsvTargetField[] {
  const mapped = new Set(mapping.filter((entry) => entry.field !== null).map((entry) => entry.field));
  return fields.filter((field) => field.required === true && !mapped.has(field.key));
}
